import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'bedtime', 'waketime',
    'score', 'scoreBar', 'statusContainer', 'statusDot', 'statusText', 'statusDescription',
    'bedtimeVariance', 'waketimeVariance', 'avgBedtime', 'avgWaketime', 'socialJetLag'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const bedtimes = this.bedtimeTargets.map((input) => this.timeToMinutes(input.value))
    const waketimes = this.waketimeTargets.map((input) => this.timeToMinutes(input.value))

    // Calculate averages and variances
    const avgBedtime = this.averageTime(bedtimes)
    const avgWaketime = this.averageTime(waketimes)
    const bedtimeVariance = this.calculateVariance(bedtimes)
    const waketimeVariance = this.calculateVariance(waketimes)

    // Social jet lag (difference between weekday and weekend midpoints)
    const weekdayBedtimes = bedtimes.slice(0, 5)
    const weekendBedtimes = bedtimes.slice(5)
    const weekdayWaketimes = waketimes.slice(0, 5)
    const weekendWaketimes = waketimes.slice(5)

    const weekdayMidpoint = this.calculateMidpoint(this.averageTime(weekdayBedtimes), this.averageTime(weekdayWaketimes))
    const weekendMidpoint = this.calculateMidpoint(this.averageTime(weekendBedtimes), this.averageTime(weekendWaketimes))
    const socialJetLag = Math.abs(weekendMidpoint - weekdayMidpoint) / 60

    // Calculate score (lower variance = higher score)
    const maxVariance = 120 // 2 hours
    const combinedVariance = (bedtimeVariance + waketimeVariance) / 2
    const score = Math.max(0, Math.round(100 - (combinedVariance / maxVariance) * 100))

    // Update display
    this.scoreTarget.textContent = score
    this.scoreBarTarget.style.width = `${score}%`
    this.bedtimeVarianceTarget.textContent = Math.round(bedtimeVariance)
    this.waketimeVarianceTarget.textContent = Math.round(waketimeVariance)
    this.avgBedtimeTarget.textContent = this.minutesToTime(avgBedtime)
    this.avgWaketimeTarget.textContent = this.minutesToTime(avgWaketime)
    this.socialJetLagTarget.textContent = socialJetLag.toFixed(1)

    this.updateStatus(score, socialJetLag)
  }

  timeToMinutes(timeStr) {
    if (!timeStr) return 0
    const [hours, mins] = timeStr.split(':').map(Number)
    // Adjust for times past midnight (e.g., 1:00 AM should be 25 hours)
    let totalMins = hours * 60 + mins
    if (hours < 6) totalMins += 24 * 60 // Assume times before 6 AM are "next day"
    return totalMins
  }

  minutesToTime(mins) {
    mins %= (24 * 60)
    const hours = Math.floor(mins / 60)
    const minutes = Math.round(mins % 60)
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`
  }

  averageTime(times) {
    if (times.length === 0) return 0
    return times.reduce((a, b) => a + b, 0) / times.length
  }

  calculateVariance(times) {
    if (times.length === 0) return 0
    const avg = this.averageTime(times)
    const squaredDiffs = times.map((t) => (t - avg) ** 2)
    return Math.sqrt(squaredDiffs.reduce((a, b) => a + b, 0) / times.length)
  }

  calculateMidpoint(bedtime, waketime) {
    // Handle crossing midnight
    if (waketime < bedtime) {
      waketime += 24 * 60
    }
    return (bedtime + waketime) / 2
  }

  updateStatus(score, socialJetLag) {
    let dotColor; let statusText; let
      description

    if (score >= 90) {
      dotColor = 'bg-green-500'
      statusText = 'EXCELLENT CONSISTENCY'
      description = 'Your sleep schedule is highly consistent. This supports optimal circadian rhythm function.'
    } else if (score >= 75) {
      dotColor = 'bg-green-400'
      statusText = 'GOOD CONSISTENCY'
      description = 'Your sleep schedule is fairly consistent. Small improvements can still help optimize your circadian rhythm.'
    } else if (score >= 60) {
      dotColor = 'bg-yellow-500'
      statusText = 'MODERATE CONSISTENCY'
      description = 'Your sleep schedule has notable variation. Consider setting consistent bed and wake alarms.'
    } else if (score >= 40) {
      dotColor = 'bg-orange-500'
      statusText = 'POOR CONSISTENCY'
      description = 'Your sleep schedule varies significantly. This can disrupt your circadian rhythm and affect health.'
    } else {
      dotColor = 'bg-red-500'
      statusText = 'HIGHLY VARIABLE'
      description = 'Your sleep schedule is very inconsistent. Prioritizing a regular schedule can significantly improve health.'
    }

    if (socialJetLag >= 2) {
      description += ` Note: Your ${socialJetLag.toFixed(1)}h social jet lag indicates a significant weekday/weekend difference.`
    }

    this.statusDotTarget.className = `w-3 h-3 ${dotColor}`
    this.statusTextTarget.textContent = statusText
    this.statusDescriptionTarget.textContent = description
  }
}
